import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";
import {
  Card,
  LabelMeta,
  LoadingState,
  EmptyState,
  theme,
} from "../components/ui";

// Documents tab is for EXTERNAL applicants only (enforced at the parent tab list).
// Behaviour per pipeline status:
//   FeedbackAdded     → CTA: go to Document Storage to send the contract docs
//   DocumentSend      → list of sent contract docs (Download)
//   DocumentRecieved  → sent docs + applicant-returned docs (Download both) + "Onboarding" CTA
//   OnboardingDone    → "Personal Info" CTA (navigates to /humanResource/{employeeId})
//   Anything else     → "Not yet at the documents stage" empty state
function DocumentsTab({ jobApplicationId, applicantData, isPostingFilled = false }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const fileBaseUrl = process.env.REACT_APP_Base_URL || apiUrl;
  const navigate = useNavigate();
  const status = applicantData?.status;
  const isReject = status === JobApplicationStatusEnum.Reject;

  const [sentContract, setSentContract] = useState(null);
  const [receivedContract, setReceivedContract] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSent = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        API.hiringContracts.getContractByApplicant(jobApplicationId)
      );
      setSentContract(response.data);
    } catch (error) {
      console.error("Error fetching sent contract:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load sent documents");
      }
    }
  }, [jobApplicationId]);

  const fetchReceived = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        API.hiringContracts.getReceiveContractByApplicant(jobApplicationId)
      );
      setReceivedContract(response.data);
    } catch (error) {
      console.error("Error fetching received contract:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load received documents");
      }
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (!jobApplicationId || status == null) return;

    const needsSent =
      status === JobApplicationStatusEnum.DocumentSend ||
      status === JobApplicationStatusEnum.DocumentRecieved ||
      status === JobApplicationStatusEnum.OnboardingDone ||
      status === JobApplicationStatusEnum.Hired;
    const needsReceived =
      status === JobApplicationStatusEnum.DocumentRecieved ||
      status === JobApplicationStatusEnum.OnboardingDone ||
      status === JobApplicationStatusEnum.Hired;

    if (!needsSent && !needsReceived) return;

    setLoading(true);
    Promise.all([
      needsSent ? fetchSent() : null,
      needsReceived ? fetchReceived() : null,
    ]).finally(() => setLoading(false));
  }, [jobApplicationId, status, fetchSent, fetchReceived]);

  // ---------- Reject lock ----------
  if (isReject) {
    return (
      <EmptyState
        icon="🚫"
        title="Candidate rejected"
        hint="No further actions are available for this application."
      />
    );
  }

  // ---------- Per-status views ----------

  // Stage 0: Documents stage not reached yet.
  if (
    status == null ||
    status < JobApplicationStatusEnum.FeedbackAdded
  ) {
    return (
      <EmptyState
        icon="📁"
        title="Documents stage not reached yet"
        hint="Documents become available once the candidate's interview feedback has been recorded."
      />
    );
  }

  // Stage: FeedbackAdded — HR needs to send contract docs from Document Storage.
  // Per Mudassir: document send/receive is a hiring pipeline action — disabled
  // once the posting is marked Filled.
  if (status === JobApplicationStatusEnum.FeedbackAdded) {
    if (isPostingFilled) {
      return (
        <EmptyState
          icon="🔒"
          title="Document send/receive locked"
          hint="This job posting has been marked as Filled — sending contract documents is disabled."
        />
      );
    }
    return (
      <div style={{ padding: "1.5rem" }}>
        <Card title="Send Contract Documents" icon="📤">
          <p
            style={{
              margin: "0 0 14px 0",
              fontSize: "0.92rem",
              color: theme.text,
              lineHeight: 1.5,
            }}
          >
            Feedback has been recorded for this candidate. The next step is to
            send the contract documents (NDA, NCA, Employee Agreement, I-9, W-4,
            Offer Letter, HR Policy) from the Document Storage page.
          </p>
          <button
            type="button"
            onClick={() => navigate("/document-store")}
            style={primaryBtn}
          >
            📤 Go to Document Storage
          </button>
        </Card>
      </div>
    );
  }

  if (loading) return <LoadingState label="Loading documents..." />;

  // Stage: DocumentSend — HR-sent docs list. Download only.
  if (status === JobApplicationStatusEnum.DocumentSend) {
    const sent = buildDocList(sentContract);
    return (
      <div style={{ padding: "1.5rem" }}>
        <Card title="Contract Documents Sent to Applicant" icon="📤">
          <LabelMeta style={{ marginBottom: "12px" }}>
            {sent.length} document{sent.length === 1 ? "" : "s"} sent · awaiting
            applicant to return signed copies
          </LabelMeta>
          {sent.length === 0 ? (
            <Muted>No documents have been sent yet.</Muted>
          ) : (
            <DocList
              docs={sent}
              fileBaseUrl={fileBaseUrl}
              showDownload
            />
          )}
        </Card>
      </div>
    );
  }

  // Stage: DocumentRecieved — both sets visible; "Onboarding" CTA.
  if (status === JobApplicationStatusEnum.DocumentRecieved) {
    const sent = buildDocList(sentContract);
    const received = buildDocList(receivedContract);
    return (
      <div style={{ padding: "1.5rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.5rem",
            marginBottom: "1.5rem",
          }}
        >
          <Card title="Sent to Applicant" icon="📤">
            {sent.length === 0 ? (
              <Muted>None.</Muted>
            ) : (
              <DocList docs={sent} fileBaseUrl={fileBaseUrl} showDownload />
            )}
          </Card>
          <Card title="Received from Applicant" icon="📥" accentColor="#2b7e3a">
            {received.length === 0 ? (
              <Muted>None.</Muted>
            ) : (
              <DocList
                docs={received}
                fileBaseUrl={fileBaseUrl}
                showDownload
                downloadLabel="Download Received"
                downloadColor="#0f5a2a"
              />
            )}
          </Card>
        </div>
        <Card title="Next Step" icon="🚀" accentColor="#7c3aed">
          <p
            style={{
              margin: "0 0 14px 0",
              fontSize: "0.92rem",
              color: theme.text,
              lineHeight: 1.5,
            }}
          >
            All contract documents have been received from the applicant. You
            can now proceed with onboarding.
          </p>
          <button
            type="button"
            onClick={() => navigate("/onboarding")}
            style={primaryBtn}
          >
            🚀 Onboarding
          </button>
        </Card>
      </div>
    );
  }

  // Stage: OnboardingDone — switch button to "Personal Info".
  // Per Mudassir (May-20 call): hide the Preview button at this stage —
  // onboarding is done, HR just needs Download access, not Preview.
  if (status === JobApplicationStatusEnum.OnboardingDone) {
    const sent = buildDocList(sentContract);
    const received = buildDocList(receivedContract);
    const empId =
      receivedContract?.jobApplicant?.jobApplicantId ||
      receivedContract?.employeeId ||
      applicantData?.jobApplicantId;
    return (
      <div style={{ padding: "1.5rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.5rem",
            marginBottom: "1.5rem",
          }}
        >
          <Card title="Sent to Applicant" icon="📤">
            {sent.length === 0 ? (
              <Muted>None.</Muted>
            ) : (
              <DocList docs={sent} fileBaseUrl={fileBaseUrl} showDownload hidePreview />
            )}
          </Card>
          <Card title="Received from Applicant" icon="📥" accentColor="#2b7e3a">
            {received.length === 0 ? (
              <Muted>None.</Muted>
            ) : (
              <DocList
                docs={received}
                fileBaseUrl={fileBaseUrl}
                showDownload
                hidePreview
                downloadLabel="Download Received"
                downloadColor="#0f5a2a"
              />
            )}
          </Card>
        </div>
        <Card title="Complete Hire" icon="👤" accentColor="#0f5a2a">
          <p
            style={{
              margin: "0 0 14px 0",
              fontSize: "0.92rem",
              color: theme.text,
              lineHeight: 1.5,
            }}
          >
            Onboarding has been completed. Fill in the personal information
            form to finalize the hire.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/humanResource/${empId}`)}
            style={primaryBtn}
          >
            👤 Personal Info
          </button>
        </Card>
      </div>
    );
  }

  // Stage: Hired — final, read-only docs (no Preview, download only).
  // Per Mudassir: Personal Info action must be hidden once the candidate is
  // hired (it lives on the OnboardingDone stage only).
  if (status === JobApplicationStatusEnum.Hired) {
    const sent = buildDocList(sentContract);
    const received = buildDocList(receivedContract);
    return (
      <div style={{ padding: "1.5rem" }}>
        <div
          style={{
            background: "#dff5e3",
            borderRadius: "16px",
            padding: "12px 16px",
            borderLeft: "3px solid #2b7e3a",
            color: "#0f5a2a",
            fontSize: "0.9rem",
            marginBottom: "1.5rem",
          }}
        >
          ✅ Candidate hired — final documents on file.
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.5rem",
            marginBottom: "1.5rem",
          }}
        >
          <Card title="Sent to Applicant" icon="📤">
            {sent.length === 0 ? (
              <Muted>None.</Muted>
            ) : (
              <DocList docs={sent} fileBaseUrl={fileBaseUrl} showDownload hidePreview />
            )}
          </Card>
          <Card title="Received from Applicant" icon="📥" accentColor="#2b7e3a">
            {received.length === 0 ? (
              <Muted>None.</Muted>
            ) : (
              <DocList
                docs={received}
                fileBaseUrl={fileBaseUrl}
                showDownload
                hidePreview
                downloadLabel="Download Received"
                downloadColor="#0f5a2a"
              />
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <EmptyState icon="📁" title="No documents available for this stage." />
  );
}

DocumentsTab.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  applicantData: PropTypes.object,
  isPostingFilled: PropTypes.bool,
};

// ============================================================
// Helpers + sub-components
// ============================================================

function buildDocList(c) {
  if (!c) return [];
  const docs = [
    { key: "ndafile", name: "NDA File", icon: "🤝", desc: "Non-Disclosure Agreement", file: c.ndafile },
    { key: "ncafile", name: "NCA File", icon: "🚫", desc: "Non-Compete Agreement", file: c.ncafile },
    { key: "employeeAgreement", name: "Employee Agreement", icon: "📋", desc: "Employment terms", file: c.employeeAgreement },
    { key: "i_nine", name: "I-9 Form", icon: "🪪", desc: "Employment Eligibility Verification", file: c.i_nine },
    { key: "w_four", name: "W-4 Form", icon: "💵", desc: "Tax Withholding", file: c.w_four },
    { key: "offerLetter", name: "Offer Letter", icon: "✉️", desc: "Compensation & start details", file: c.offerLetter },
    { key: "hrPolicy", name: "HR Policy", icon: "📘", desc: "Company policy handbook", file: c.hrPolicy },
  ];
  return docs.filter((d) => d.file);
}

function DocList({ docs, fileBaseUrl, showDownload, hidePreview, downloadLabel, downloadColor }) {
  // Use a direct anchor-tag download. fetch() trips CORS on the file host
  // (the same approach is used elsewhere in ViewCandidate.js).
  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.warning("No file to download");
      return;
    }
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName || "document";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const handlePreview = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {docs.map((doc) => {
        const fileUrl = `${fileBaseUrl}/${doc.file}`;
        return (
          <div
            key={doc.key}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#fafdfb",
              borderLeft: "3px solid #7c9bc0",
              borderRadius: "14px",
              padding: "10px 14px",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                minWidth: "200px",
              }}
            >
              <span style={{ fontSize: "1.1rem" }}>{doc.icon}</span>
              <div>
                <div
                  style={{
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: theme.accentDeep,
                  }}
                >
                  {doc.name}
                </div>
                <div style={{ fontSize: "0.72rem", color: theme.textMuted }}>
                  {doc.desc}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              {!hidePreview && (
                <button
                  type="button"
                  onClick={() => handlePreview(fileUrl)}
                  style={btnGhost}
                >
                  👁 Preview
                </button>
              )}
              {showDownload && (
                <button
                  type="button"
                  onClick={() => handleDownload(fileUrl, doc.name)}
                  style={{
                    ...btnPrimary,
                    background: downloadColor || "#2f7bc5",
                    borderColor: downloadColor || "#2f7bc5",
                  }}
                >
                  ⬇ {downloadLabel || "Download"}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
DocList.propTypes = {
  docs: PropTypes.array,
  fileBaseUrl: PropTypes.string,
  showDownload: PropTypes.bool,
  hidePreview: PropTypes.bool,
  downloadLabel: PropTypes.string,
  downloadColor: PropTypes.string,
};

function Muted({ children }) {
  return (
    <div style={{ color: theme.textMuted, fontSize: "0.85rem" }}>{children}</div>
  );
}
Muted.propTypes = { children: PropTypes.node };

const primaryBtn = {
  background: "#0f5a2a",
  color: "white",
  border: "none",
  padding: "10px 22px",
  borderRadius: "40px",
  fontSize: "0.88rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 2px 6px rgba(15,90,42,0.18)",
};

const btnGhost = {
  background: "#fff",
  border: `1px solid #2b7e3a`,
  color: "#2b7e3a",
  padding: "6px 14px",
  borderRadius: "40px",
  fontSize: "0.76rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const btnPrimary = {
  color: "white",
  border: `1px solid #2f7bc5`,
  background: "#2f7bc5",
  padding: "6px 14px",
  borderRadius: "40px",
  fontSize: "0.76rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

export default DocumentsTab;
